import { makeStyles, PositioningShorthand, PositioningShorthandValue, TeachingPopover, TeachingPopoverBody, TeachingPopoverFooter, TeachingPopoverHeader, TeachingPopoverSurface, TeachingPopoverTitle, TeachingPopoverTrigger } from "@fluentui/react-components";
import { FC, useCallback, useEffect, useState } from "react";
import { performEnterpriseGPTApiCall } from "../../Hooks/useMiddlewareApiCall";
import { UserPreferences } from "../../EnterpriseGPTApi/EnterpriseGPTApiV1Client";
import { setUserPreferencesAction } from "../../store/actions/userPreferencesAction";
import { useSurvey, useUserPreferences } from "../../store/hooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { IFeatureHighlights } from "../../EnterpriseGPTApi/EnterpriseGPTApiV1Client";
import { appInsights } from "../../AppInsights";
import { EventName_UserInteraction, EventProperty_Value_ApplicationType, EventProperty_Value_FeatureHighlightDismiss, EventProperty_Value_FeatureHighlightOpen, EventProperty_Value_UploadDocument, UserInteractionEventProperties } from "../../store/models/customEvents";
import { useUserContext } from "../../Contexts/UserContext";

const useStyles = makeStyles({
    popoverSurface: {
        boxShadow: '0 0 10px'
    }
});

// renders the feature highlight popup, which is shown to the user when a new feature is available
// the component needs to be injected in the component that uses the feature
export interface FeatureHighlightPopupProps {
    id: keyof IFeatureHighlights;
    header: string;
    title: string;
    content: string;
    position?: PositioningShorthand;
    link: string;
    children: React.JSX.Element;
}

export const FeatureHighlightPopup: FC<FeatureHighlightPopupProps> = ({ id, header, title, content, position, link, children }: FeatureHighlightPopupProps) => {
    const classes = useStyles();
    const { userPreferences, dispatch } = useUserPreferences();
    const { survey } = useSurvey();
    const { userProfile } = useUserContext();
    const { dispatchNotification } = useNotification();
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        // Do not show any feature highlights if a survey is active, since survey dialog will interfere with the feature highlight dialog
        if (!survey) {
            const highlightFeature = userPreferences?.featureHighlights?.[id] ?? false;
            setIsOpen(highlightFeature);
            if (highlightFeature) {
                appInsights.trackEvent({
                    name: EventName_UserInteraction, properties:
                        {
                            applicationType: EventProperty_Value_ApplicationType,
                            userId: userProfile?.username,
                            interactionType: EventProperty_Value_FeatureHighlightOpen,
                            interactionValue: id,
                        } as UserInteractionEventProperties
                });
            }
        }
    }, [userPreferences, survey, id]);

    const onLearnMoreClick = () => {
        window.open(link, '_blank');
    };

    // when the user dismisses the feature highlight, update the user preferences
    const updateUserPreferences = useCallback(() => {
        performEnterpriseGPTApiCall(async (client) => {
            try {
                const updatedUserPreferences = {
                    ...userPreferences,
                    featureHighlights: {
                        ...userPreferences?.featureHighlights,
                        [id]: false,
                    }
                };
                await client.updateUserPreferences(new UserPreferences(updatedUserPreferences));
                dispatch(setUserPreferencesAction(updatedUserPreferences));
                appInsights.trackEvent({
                    name: EventName_UserInteraction, properties:
                        {
                            applicationType: EventProperty_Value_ApplicationType,
                            userId: userProfile?.username,
                            interactionType: EventProperty_Value_FeatureHighlightDismiss,
                            interactionValue: id,
                        } as UserInteractionEventProperties
                });
            } catch (e: unknown) {
                if ((e as Error).message !== 'canceled') {
                    dispatchNotification('error.api.patch.userPreferences', 'error');
                }
            }
        });
    }, [survey, userPreferences, id]);

    return (
        <TeachingPopover open={isOpen} positioning={position}>
            <TeachingPopoverTrigger>
                <div>
                    {children}
                </div>
            </TeachingPopoverTrigger>
            <TeachingPopoverSurface className={classes.popoverSurface}>
                <TeachingPopoverHeader onClick={updateUserPreferences}>{header}</TeachingPopoverHeader>
                <TeachingPopoverBody>
                    <TeachingPopoverTitle>{title}</TeachingPopoverTitle>
                    <div dangerouslySetInnerHTML={{__html:content}}></div>
                </TeachingPopoverBody>
                <TeachingPopoverFooter primary={{ onClick: onLearnMoreClick, children: "Learn more" }} secondary={{ onClick: updateUserPreferences, children: "Dismiss" }} />
            </TeachingPopoverSurface>
        </TeachingPopover>
    );
}
